import { App, Card, Switch, Typography, theme } from 'antd';
import { ListGroup, ListGroupItem } from '../../../components/CssFrameworkComponents';
import { SwitchWithRole } from '../../../components/ButtonWithRole';
import USER_ROLES from '../../auth/userRoles';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';
import { useHasAiInsightsAccess } from '~features/ai-insights';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { saveSettings, setEmailPreferences } from '../redux/settingsActions';
import { useGetCommsConsentQuery, useSetMarketingConsentMutation } from '../../my-account/api/myAccountApiSlice';

// Placeholder copy pending CS confirmation — kept as a single named constant (title + accessible name).
const USAGE_ALERT_EMAILS_LABEL = 'Usage alert emails';

const EmailPreferencesTab = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const prerenderUser = usePrerenderUser();
  const canUseAiInsights = useHasAiInsightsAccess();
  const settings = useSelector((state) => state.settings);

  const { data: commsConsent, isFetching: isFetchingConsent } = useGetCommsConsentQuery();
  const [saveMarketingConsent, { isLoading: isSavingConsent }] = useSetMarketingConsentMutation();

  // Local state mirrors the fetched value so the switch updates instantly on toggle;
  // it is reconciled from the server response (and reverted on save failure).
  const [marketingConsent, setMarketingConsent] = useState(false);
  useEffect(() => {
    if (commsConsent) {
      setMarketingConsent(commsConsent.marketing);
    }
  }, [commsConsent]);

  const updateMarketingConsent = useCallback(
    async (value) => {
      setMarketingConsent(value); // optimistic
      try {
        await saveMarketingConsent({ marketing: value }).unwrap();
        message.success('Communication preferences saved successfully');
      } catch {
        setMarketingConsent(!value); // revert on failure
        message.error('An error occurred while saving communication preferences');
      }
    },
    [saveMarketingConsent]
  );

  const updateEmailPreferences = useCallback(
    (update) => {
      const newEmailPreferences = {
        ...settings.emailPreferences,
        ...update,
      };
      dispatch(setEmailPreferences(newEmailPreferences));
      dispatch(
        saveSettings({
          settings: {
            ...settings,
            emailPreferences: newEmailPreferences,
          },
          userId: prerenderUser.id,
        })
      );
    },
    [settings, prerenderUser.id, setEmailPreferences, saveSettings]
  );

  return (
    <>
    <Card>
      <ListGroup>
        <ListGroupItem>
          <div className="col">
            <Typography.Title level={5}>{USAGE_ALERT_EMAILS_LABEL}</Typography.Title>
            <Typography.Text type="secondary">
              Do you want an email update when your usage approaches your plan limit?
            </Typography.Text>
          </div>
          <div className="col-auto">
            <SwitchWithRole
              aria-label={USAGE_ALERT_EMAILS_LABEL}
              disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.TEAM_MEMBER, USER_ROLES.GUEST]}
              checked={settings.emailPreferences.extraRenderEmailOptIn !== false}
              onClick={() =>
                updateEmailPreferences({
                  extraRenderEmailOptIn: settings.emailPreferences.extraRenderEmailOptIn === false,
                })
              }
              disabled={prerenderUser.inProgress}
            />
          </div>
        </ListGroupItem>
        {prerenderUser.isMeteredBilling && (
          <ListGroupItem>
            <div className="col">
              <Typography.Title level={5}>Better Plan Update</Typography.Title>
              <Typography.Text type="secondary">
                Do you want an email update if we find a better plan according to your usage?
              </Typography.Text>
            </div>
            <div className="col-auto">
              <SwitchWithRole
                disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.TEAM_MEMBER, USER_ROLES.GUEST]}
                checked={settings.emailPreferences.sendSuggested}
                onClick={() => updateEmailPreferences({ sendSuggested: !settings.emailPreferences.sendSuggested })}
                disabled={prerenderUser.inProgress}
              />
            </div>
          </ListGroupItem>
        )}
        <ListGroupItem>
          <div className="col">
            <Typography.Title level={5}>Page Report</Typography.Title>
            <Typography.Text type="secondary">
              Do you want an email update about your problematic pages?
            </Typography.Text>
          </div>
          <div className="col-auto">
            <SwitchWithRole
              disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.TEAM_MEMBER, USER_ROLES.GUEST]}
              checked={!!settings.emailPreferences.emailReportPeriod}
              onClick={() =>
                updateEmailPreferences({
                  emailReportPeriod: settings.emailPreferences.emailReportPeriod ? null : 'WEEKLY',
                })
              }
              disabled={
                prerenderUser.inProgress ||
                // Non weekly report is set by us, so it can't be managed
                (settings.emailPreferences.emailReportPeriod !== 'WEEKLY' &&
                  settings.emailPreferences.emailReportPeriod !== undefined &&
                  settings.emailPreferences.emailReportPeriod !== null)
              }
            />
          </div>
        </ListGroupItem>
        {canUseAiInsights && (
          <ListGroupItem>
            <div className="col">
              <Typography.Title level={5}>AI Discoverability Status Update</Typography.Title>
              <Typography.Text type="secondary">
                Do you want to receive a biweekly email containing your AI discoverability status?
              </Typography.Text>
            </div>
            <div className="col-auto">
              <SwitchWithRole
                aria-label="AI Discoverability Status Update"
                disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.TEAM_MEMBER, USER_ROLES.GUEST]}
                checked={settings.emailPreferences.aiDiscoverabilityStatusUpdate !== false}
                onClick={() =>
                  updateEmailPreferences({
                    aiDiscoverabilityStatusUpdate: settings.emailPreferences.aiDiscoverabilityStatusUpdate === false,
                  })
                }
                disabled={prerenderUser.inProgress}
              />
            </div>
          </ListGroupItem>
        )}
      </ListGroup>
    </Card>
    {/* Product consent is mandatory at signup for new users; a falsy value means a legacy
        user who predates the consent flow, so the whole section is hidden for them. */}
    {commsConsent?.product && (
      <Card title="Communication Preferences" style={{ marginTop: token.marginLG }}>
        <ListGroup>
          <ListGroupItem>
            <div className="col">
              <Typography.Title level={5}>Product Communications</Typography.Title>
              <Typography.Text type="secondary">
                Essential product and service messages — account, billing, security and other transactional
                communications. Consent for these is required and cannot be revoked.
              </Typography.Text>
            </div>
            <div className="col-auto">
              <Switch checked={!!commsConsent?.product} loading={isFetchingConsent} disabled />
            </div>
          </ListGroupItem>
          <ListGroupItem>
            <div className="col">
              <Typography.Title level={5}>Marketing Communications</Typography.Title>
              <Typography.Text type="secondary">
                Product news, tips, offers and other promotional messages. You can opt out at any time.
              </Typography.Text>
            </div>
            <div className="col-auto">
              <Switch
                checked={marketingConsent}
                loading={isFetchingConsent || isSavingConsent}
                disabled={isFetchingConsent || isSavingConsent}
                onChange={updateMarketingConsent}
              />
            </div>
          </ListGroupItem>
        </ListGroup>
      </Card>
    )}
    </>
  );
};

export default EmailPreferencesTab;
